import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaMoneyBillWave, FaChartLine, FaPiggyBank, FaUniversity, 
  FaUserTie, FaPercent, FaReceipt, FaCalculator, FaBuilding,
  FaFileInvoiceDollar, FaFileInvoice, FaFemale, FaCertificate,
  FaSeedling, FaUserShield, FaEnvelopeOpenText, FaUmbrella,
  FaHandHoldingHeart, FaBalanceScale, FaCoins, FaLayerGroup, FaChartPie,
  FaCreditCard, FaFire, FaShieldAlt, FaHome as FaHouse, FaHandshake, FaBriefcase,
  FaRupeeSign, FaArrowUp, FaExchangeAlt, FaBullseye, FaStore, FaShoppingBag, FaChartBar, FaKey,
  FaGasPump, FaUtensils, FaIdBadge, FaWifi, FaPills, FaTaxi, FaFileSignature, FaBook, FaBroom,
  FaGraduationCap, FaTint, FaBullhorn, FaUsers
} from 'react-icons/fa';
import SEO from '../components/SEO';
import FeatureSlider from '../components/FeatureSlider';

const calculators = [
  // Loan Calculators
  {
    path: '/emi',
    icon: FaMoneyBillWave,
    title: 'EMI Calculator',
    description: 'Calculate your Equated Monthly Installment for home, car, or personal loans.',
    color: '#2563eb',
  },
  {
    path: '/loan-prepayment',
    icon: FaCreditCard,
    title: 'Loan Prepayment',
    description: 'Calculate savings from prepaying your loan and reduce tenure or EMI.',
    color: '#7c3aed',
  },
  {
    path: '/home-loan-eligibility',
    icon: FaKey,
    title: 'Home Loan Eligibility',
    description: 'Check how much home loan you can get based on your income.',
    color: '#0891b2',
  },
  {
    path: '/loan-estimator',
    icon: FaCalculator,
    title: 'Customer Loan Estimator',
    description: 'Estimate EMI, repayment burden, and affordable loan amount for personal, car, and home loans.',
    color: '#2563eb',
  },
  // Investment Calculators
  {
    path: '/sip',
    icon: FaChartLine,
    title: 'SIP Calculator',
    description: 'Plan your Systematic Investment Plan and see your wealth grow over time.',
    color: '#10b981',
  },
  {
    path: '/lumpsum',
    icon: FaPiggyBank,
    title: 'Lumpsum Calculator',
    description: 'Calculate returns on one-time mutual fund investments.',
    color: '#f59e0b',
  },
  {
    path: '/goal-sip',
    icon: FaBullseye,
    title: 'Goal SIP Planner',
    description: 'Calculate SIP needed to achieve your financial goals like education, house, car.',
    color: '#f97316',
  },
  {
    path: '/swp',
    icon: FaExchangeAlt,
    title: 'SWP Calculator',
    description: 'Plan Systematic Withdrawal from your corpus for regular monthly income.',
    color: '#14b8a6',
  },
  {
    path: '/elss',
    icon: FaShieldAlt,
    title: 'ELSS Tax Saver',
    description: 'Calculate returns and tax savings from ELSS mutual funds under 80C.',
    color: '#22c55e',
  },
  {
    path: '/fd',
    icon: FaUniversity,
    title: 'FD Calculator',
    description: 'Calculate Fixed Deposit maturity amount with compound interest.',
    color: '#f97316',
  },
  {
    path: '/rd',
    icon: FaBuilding,
    title: 'RD Calculator',
    description: 'Calculate Recurring Deposit returns for regular monthly savings.',
    color: '#6366f1',
  },
  // Retirement & FIRE
  {
    path: '/ppf',
    icon: FaPiggyBank,
    title: 'PPF Calculator',
    description: 'Calculate your Public Provident Fund maturity amount with tax benefits.',
    color: '#8b5cf6',
  },
  {
    path: '/epf',
    icon: FaUserTie,
    title: 'EPF Calculator',
    description: 'Estimate your Employee Provident Fund corpus at retirement.',
    color: '#ec4899',
  },
  {
    path: '/nps',
    icon: FaUserTie,
    title: 'NPS Calculator',
    description: 'Plan your National Pension System investment for retirement.',
    color: '#14b8a6',
  },
  {
    path: '/retirement',
    icon: FaUmbrella,
    title: 'Retirement Planner',
    description: 'Calculate corpus needed for comfortable retirement with inflation adjustment.',
    color: '#0ea5e9',
  },
  {
    path: '/fire',
    icon: FaFire,
    title: 'FIRE Calculator',
    description: 'Financial Independence, Retire Early - Plan your freedom journey.',
    color: '#f97316',
  },
  {
    path: '/fixed-income',
    icon: FaMoneyBillWave,
    title: 'Fixed Monthly Income',
    description: 'Calculate corpus needed for fixed monthly income or income from existing corpus.',
    color: '#06b6d4',
  },
  {
    path: '/gratuity',
    icon: FaReceipt,
    title: 'Gratuity Calculator',
    description: 'Calculate your gratuity amount based on years of service.',
    color: '#84cc16',
  },
  // Government Saving Schemes
  {
    path: '/ssy',
    icon: FaFemale,
    title: 'SSY Calculator',
    description: 'Sukanya Samriddhi Yojana - Girl child savings scheme under Beti Bachao Beti Padhao.',
    color: '#ec4899',
  },
  {
    path: '/nsc',
    icon: FaCertificate,
    title: 'NSC Calculator',
    description: 'National Savings Certificate - 5 year government savings bond with 80C benefit.',
    color: '#f59e0b',
  },
  {
    path: '/kvp',
    icon: FaSeedling,
    title: 'KVP Calculator',
    description: 'Kisan Vikas Patra - Doubles your money at guaranteed interest rate.',
    color: '#22c55e',
  },
  {
    path: '/scss',
    icon: FaUserShield,
    title: 'SCSS Calculator',
    description: 'Senior Citizens Savings Scheme - Highest interest rate for 60+ year citizens.',
    color: '#6366f1',
  },
  {
    path: '/pomis',
    icon: FaEnvelopeOpenText,
    title: 'POMIS Calculator',
    description: 'Post Office Monthly Income Scheme - Guaranteed monthly income for 5 years.',
    color: '#0ea5e9',
  },
  {
    path: '/potd',
    icon: FaUniversity,
    title: 'Post Office TD',
    description: 'Post Office Time Deposit - Fixed deposit with sovereign guarantee.',
    color: '#dc2626',
  },
  {
    path: '/pmvvy',
    icon: FaHandHoldingHeart,
    title: 'PM Vaya Vandana',
    description: 'PMVVY - Guaranteed pension scheme for senior citizens by LIC.',
    color: '#8b5cf6',
  },
  {
    path: '/lic-plan',
    icon: FaHandHoldingHeart,
    title: 'LIC Plan Calculator',
    description: 'Estimate LIC premium paid, bonus, maturity value, and expected returns.',
    color: '#7c3aed',
  },
  {
    path: '/apy',
    icon: FaUmbrella,
    title: 'APY Calculator',
    description: 'Atal Pension Yojana - Guaranteed pension for unorganized sector workers.',
    color: '#14b8a6',
  },
  // Tax Calculators
  {
    path: '/income-tax',
    icon: FaPercent,
    title: 'Income Tax Calculator',
    description: 'Calculate income tax under both Old and New tax regimes.',
    color: '#ef4444',
  },
  {
    path: '/gst',
    icon: FaReceipt,
    title: 'GST Calculator',
    description: 'Calculate GST inclusive or exclusive of base amount.',
    color: '#a855f7',
  },
  {
    path: '/hra',
    icon: FaBuilding,
    title: 'HRA Calculator',
    description: 'Calculate House Rent Allowance exemption for tax savings.',
    color: '#0ea5e9',
  },
  {
    path: '/tds',
    icon: FaRupeeSign,
    title: 'TDS Calculator',
    description: 'Calculate TDS on salary, FD, rent, professional fees and more.',
    color: '#dc2626',
  },
  {
    path: '/tds-rent',
    icon: FaHouse,
    title: 'TDS on Rent',
    description: 'Calculate TDS on rent under Section 194I and 194IB.',
    color: '#f59e0b',
  },
  {
    path: '/professional-tax',
    icon: FaBriefcase,
    title: 'Professional Tax',
    description: 'Calculate state-wise Professional Tax deduction from salary.',
    color: '#6366f1',
  },
  {
    path: '/capital-gain',
    icon: FaChartLine,
    title: 'Capital Gain Tax',
    description: 'Calculate STCG/LTCG tax on stocks, MF, real estate & gold per Budget 2024.',
    color: '#dc2626',
  },
  // Real Estate
  {
    path: '/stamp-duty',
    icon: FaCertificate,
    title: 'Stamp Duty Calculator',
    description: 'Calculate state-wise stamp duty and registration charges.',
    color: '#8b5cf6',
  },
  {
    path: '/rent-vs-buy',
    icon: FaHandshake,
    title: 'Rent vs Buy',
    description: 'Compare renting vs buying a house - which is better for you?',
    color: '#059669',
  },
  {
    path: '/rental-yield',
    icon: FaChartBar,
    title: 'Rental Yield',
    description: 'Calculate ROI and yield on your rental property investment.',
    color: '#10b981',
  },
  {
    path: '/water-storage',
    icon: FaTint,
    title: 'Water Storage',
    description: 'Calculate tank water storage in liters for rectangle or round tanks.',
    color: '#0ea5e9',
  },
  // Salary & Business
  {
    path: '/salary',
    icon: FaFileInvoiceDollar,
    title: 'Salary Calculator',
    description: 'Calculate salary components and generate professional salary slips.',
    color: '#059669',
  },
  {
    path: '/invoice',
    icon: FaFileInvoice,
    title: 'Invoice Generator',
    description: 'Create GST-compliant invoices, bills, and quotations with PDF export.',
    color: '#7c3aed',
  },
  {
    path: '/business-poster',
    icon: FaBullhorn,
    title: 'Business Poster Generator',
    description: 'Design eye-catching promotional posters for sales, events, services, and local offers.',
    color: '#f43f5e',
  },
  {
    path: '/restaurant-menu-generator',
    icon: FaUtensils,
    title: 'Restaurant Menu Generator',
    description: 'Create elegant restaurant menus with premium layouts, branding, and export-ready downloads.',
    color: '#ea580c',
  },
  {
    path: '/group-collection-report',
    icon: FaUsers,
    title: 'Event Fund Report',
    description: 'Track shared money, event expenses, and fair distribution for pujas, parties, or group outings.',
    color: '#8b5cf6',
  },
  {
    path: '/markup',
    icon: FaStore,
    title: 'Markup & Margin',
    description: 'Calculate markup, margin, and profit for your business pricing.',
    color: '#10b981',
  },
  {
    path: '/breakeven',
    icon: FaBalanceScale,
    title: 'Break-even Analysis',
    description: 'Calculate when your business becomes profitable.',
    color: '#f59e0b',
  },
  // Bill & Receipt Generators
  {
    path: '/general-bill',
    icon: FaReceipt,
    title: 'General Bill Generator',
    description: 'Universal bill maker - invoice, receipt, sales/service bill with GST, VAT and multi-currency.',
    color: '#1d4ed8',
  },
  {
    path: '/shop-bill',
    icon: FaShoppingBag,
    title: 'Shop Bill Generator',
    description: 'Create grocery bills and retail shop bills with itemized totals.',
    color: '#2563eb',
  },
  {
    path: '/quotation',
    icon: FaFileSignature,
    title: 'Quotation Generator',
    description: 'Create professional quotations with GST, validity period and item-wise pricing.',
    color: '#1d4ed8',
  },
  {
    path: '/rent-receipt',
    icon: FaFileInvoice,
    title: 'Rent Receipt Generator',
    description: 'Generate HRA-ready monthly or quarterly rent receipts with landlord PAN.',
    color: '#0ea5e9',
  },
  {
    path: '/salary-slip',
    icon: FaIdBadge,
    title: 'Salary Slip Generator',
    description: 'Create professional payslips with PF, ESI, tax and full salary breakdown.',
    color: '#059669',
  },
  {
    path: '/petrol-bill',
    icon: FaGasPump,
    title: 'Fuel Bill Generator',
    description: 'Generate petrol, diesel and CNG fuel bills with pump details and tax.',
    color: '#dc2626',
  },
  {
    path: '/restaurant-bill',
    icon: FaUtensils,
    title: 'Restaurant Bill Generator',
    description: 'Create restaurant, cafe and food bills with GST split, service charge and KOT.',
    color: '#ea580c',
  },
  {
    path: '/medical-bill',
    icon: FaPills,
    title: 'Medical Bill Generator',
    description: 'Pharmacy and hospital bills with batch, expiry, GST and patient details.',
    color: '#10b981',
  },
  {
    path: '/internet-bill',
    icon: FaWifi,
    title: 'Internet Bill Generator',
    description: 'Broadband, Wi-Fi and ISP monthly bills with plan details and GST.',
    color: '#0e7490',
  },
  {
    path: '/cab-bill',
    icon: FaTaxi,
    title: 'Cab Bill Generator',
    description: 'Taxi, cab and ride bills with distance, waiting, toll and surge charges.',
    color: '#f59e0b',
  },
  {
    path: '/daily-helper-bill',
    icon: FaBroom,
    title: 'Daily Helper Bill',
    description: 'Salary bills for house maids, cooks, drivers and nannies with attendance & advances.',
    color: '#0f766e',
  },
  {
    path: '/library-book-receipt',
    icon: FaBook,
    title: 'Library Book Receipt',
    description: 'Library issue, return, reservation and fine receipts with book metadata.',
    color: '#0e7490',
  },
  {
    path: '/school-fees-receipt',
    icon: FaGraduationCap,
    title: 'School Fees Receipt',
    description: 'Tuition, exam, transport, hostel, admission and fine receipts with fee head breakdown.',
    color: '#1d4ed8',
  },
  // Interest & Inflation
  {
    path: '/simple-interest',
    icon: FaCalculator,
    title: 'Simple Interest',
    description: 'Calculate simple interest on your principal amount.',
    color: '#22c55e',
  },
  {
    path: '/compound-interest',
    icon: FaCalculator,
    title: 'Compound Interest',
    description: 'Calculate compound interest with different compounding frequencies.',
    color: '#3b82f6',
  },
  {
    path: '/inflation',
    icon: FaArrowUp,
    title: 'Inflation Calculator',
    description: 'Calculate how inflation affects your money\'s purchasing power.',
    color: '#ef4444',
  },
  // Stock & Mutual Fund Calculators
  {
    path: '/stock-profit',
    icon: FaBalanceScale,
    title: 'Stock Profit/Loss',
    description: 'Calculate stock trading profit/loss with brokerage, STT, and all charges.',
    color: '#ef4444',
  },
  {
    path: '/ecommerce-profit',
    icon: FaStore,
    title: 'E-commerce Profit',
    description: 'Estimate net margin for Flipkart, Amazon India and Meesho after platform fees.',
    color: '#2563eb',
  },
  {
    path: '/cagr',
    icon: FaChartLine,
    title: 'CAGR Calculator',
    description: 'Calculate Compound Annual Growth Rate for your investments.',
    color: '#8b5cf6',
  },
  {
    path: '/dividend-yield',
    icon: FaCoins,
    title: 'Dividend Yield',
    description: 'Calculate dividend yield and project future dividend income.',
    color: '#f59e0b',
  },
  {
    path: '/stock-average',
    icon: FaLayerGroup,
    title: 'Stock Average',
    description: 'Calculate average price for multiple stock purchases.',
    color: '#06b6d4',
  },
  {
    path: '/mf-returns',
    icon: FaChartPie,
    title: 'MF Returns',
    description: 'Calculate mutual fund returns for SIP and lumpsum investments.',
    color: '#22c55e',
  },
  {
    path: '/business-poster',
    icon: FaBullhorn,
    title: 'Business Poster Generator',
    description: 'Design branded posters for offers, events, and local business promotions.',
    color: '#ef4444',
  },
  {
    path: '/restaurant-menu-generator',
    icon: FaUtensils,
    title: 'Restaurant Menu Generator',
    description: 'Create premium restaurant menus with logo, color themes, and easy download options.',
    color: '#f59e0b',
  },
  {
    path: '/group-collection-report',
    icon: FaUsers,
    title: 'Group Collection Report',
    description: 'Track pooled contributions and share a clean report for events, puja, or get-togethers.',
    color: '#10b981',
  },
];

const recentToolPaths = new Set([
  '/business-poster',
  '/restaurant-menu-generator',
  '/group-collection-report',
]);

const categoryTabs = [
  { label: 'All', value: 'all' },
  { label: 'Finance', value: 'finance' },
  { label: 'Tax', value: 'tax' },
  { label: 'Business', value: 'business' },
  { label: 'Bills', value: 'bills' },
];

const getToolCategory = (path) => {
  if (['/business-poster', '/restaurant-menu-generator', '/group-collection-report', '/invoice', '/quotation', '/salary', '/salary-slip', '/rent-receipt', '/markup', '/breakeven', '/shop-bill', '/general-bill', '/restaurant-bill', '/medical-bill', '/internet-bill', '/cab-bill', '/daily-helper-bill', '/library-book-receipt', '/school-fees-receipt'].includes(path)) {
    return 'business';
  }

  if (['/income-tax', '/gst', '/hra', '/tds', '/tds-rent', '/professional-tax', '/capital-gain'].includes(path)) {
    return 'tax';
  }

  if (['/general-bill', '/shop-bill', '/petrol-bill', '/restaurant-bill', '/medical-bill', '/internet-bill', '/cab-bill', '/daily-helper-bill', '/library-book-receipt', '/school-fees-receipt'].includes(path)) {
    return 'bills';
  }

  return 'finance';
};

const featuredTools = calculators.filter((calc) => recentToolPaths.has(calc.path));

const homeFaqs = [
  {
    question: 'Is paisapyara free to use?',
    answer: 'Yes. All calculators, bill generators, and blog content on paisapyara are free to use without registration.',
  },
  {
    question: 'Do I need to create an account to use the calculators?',
    answer: 'No. You can open any calculator or generator and use it directly in your browser without signing up.',
  },
  {
    question: 'Are my calculations and bill details stored on your server?',
    answer: 'No. paisapyara runs calculations client-side in your browser, which helps keep your financial inputs and bill data private.',
  },
  {
    question: 'Can I download reports or bills as PDF?',
    answer: 'Yes. Many calculators and generators include PDF export so you can save, print, or share your results easily.',
  },
  {
    question: 'Which types of tools are available on paisapyara?',
    answer: 'paisapyara includes loan, investment, tax, retirement, real-estate, salary, invoice, receipt, and utility bill tools designed for Indian users.',
  },
  {
    question: 'Are tax slabs and financial assumptions updated for India?',
    answer: 'Major tax and finance tools are maintained for Indian users and updated for recent financial years, but you should still verify critical decisions with official sources or a professional advisor.',
  },
];

const Home = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredCalculators = useMemo(() => {
    const term = searchText.trim().toLowerCase();

    return calculators.filter((calc) => {
      const matchesCategory = selectedCategory === 'all' || getToolCategory(calc.path) === selectedCategory;
      const matchesSearch = !term || calc.title.toLowerCase().includes(term) || calc.description.toLowerCase().includes(term);
      return matchesCategory && matchesSearch;
    });
  }, [searchText, selectedCategory]);

  return (
    <div className="home-page">
      <SEO 
        title="#Paisa Pyara"
        description="#Paisa Pyara - 100+ Indian calculators & bill generators, free & private. EMI, SIP, PPF, EPF, NPS, Income Tax, GST, HRA, FD, RD + invoice, rent receipt, salary slip, fuel, restaurant, medical, cab, quotation, library and school fees receipt generators. PDF export, FY 2025-26."
        keywords="#Paisa Pyara, Indian financial calculator, EMI calculator, SIP calculator, PPF calculator, EPF calculator, NPS calculator, income tax calculator, GST calculator, HRA calculator, gratuity calculator, FD calculator, RD calculator, mutual fund calculator, loan calculator India, investment calculator, tax calculator India, retirement calculator, salary calculator, bill generator, invoice generator, rent receipt generator, salary slip generator, fuel bill, restaurant bill, medical bill, cab bill, quotation generator, library receipt, daily helper bill, school fees receipt, FY 2025-26"
        canonicalPath="/"
        faqItems={homeFaqs}
      />
      <div className="home-header">
        <h1>#Paisa Pyara</h1>
        <p>100+ Indian calculators &amp; bill generators, free &amp; private</p>
        <div className="home-stats">
          <div className="home-stat">
            <span className="home-stat-number">100+</span>
            <span className="home-stat-label">Calculators &amp; Generators</span>
          </div>
          <div className="home-stat">
            <span className="home-stat-number">100%</span>
            <span className="home-stat-label">Free &amp; Private</span>
          </div>
          <div className="home-stat">
            <span className="home-stat-number">FY 25-26</span>
            <span className="home-stat-label">Updated</span>
          </div>
        </div>
      </div>

      <FeatureSlider />

      {featuredTools.length > 0 && (
        <section className="featured-tools-section">
          <div className="home-spotlight">
            <div className="home-spotlight-copy">
              <span className="home-spotlight-eyebrow">New on paisapyara</span>
              <h2>Smarter tools for today’s business and personal planning</h2>
              <p>
                Create polished posters, restaurant menus, and event collection reports in minutes without leaving the site.
              </p>
              <div className="home-spotlight-actions">
                <Link to="/business-poster" className="home-spotlight-primary">Create poster</Link>
                <Link to="/restaurant-menu-generator" className="home-spotlight-secondary">Menu generator</Link>
              </div>
            </div>
            <div className="home-spotlight-stats">
              <div>
                <strong>3</strong>
                <span>Fresh tools</span>
              </div>
              <div>
                <strong>100%</strong>
                <span>Free to use</span>
              </div>
              <div>
                <strong>1-click</strong>
                <span>Share &amp; download</span>
              </div>
            </div>
          </div>

          <div className="home-section-header home-section-header-inline">
            <h2>Latest tools</h2>
            <span>Newest additions</span>
          </div>

          <div className="featured-tools-grid">
            {featuredTools.map((calc, index) => (
              <Link
                to={calc.path}
                key={`${calc.path}-${index}`}
                className="featured-tool-card"
              >
                <span className="featured-tool-badge">New</span>
                <div className="featured-tool-icon" style={{ background: `linear-gradient(135deg, ${calc.color}, ${calc.color}dd)` }}>
                  <calc.icon />
                </div>
                <div className="featured-tool-content">
                  <h3>{calc.title}</h3>
                  <p>{calc.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="home-search-panel">
        <div className="home-search-box">
          <span className="home-search-icon">🔎</span>
          <input
            type="text"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search calculators and generators..."
            aria-label="Search tools"
          />
        </div>

        <div className="home-category-pills">
          {categoryTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`category-pill ${selectedCategory === tab.value ? 'active' : ''}`}
              onClick={() => setSelectedCategory(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="home-section-header">
        <h2>Popular &amp; newly added tools</h2>
      </div>

      <div className="calculator-grid">
        {filteredCalculators.map((calc, index) => {
          const isFeatured = recentToolPaths.has(calc.path);

          return (
            <Link
              to={calc.path}
              key={`${calc.path}-${index}`}
              className={`calculator-card ${isFeatured ? 'calculator-card-featured' : ''}`}
            >
              {isFeatured && <span className="tool-badge">New</span>}
              <div className="icon" style={{ background: `linear-gradient(135deg, ${calc.color}, ${calc.color}dd)` }}>
                <calc.icon />
              </div>
              <h3>{calc.title}</h3>
              <p>{calc.description}</p>
            </Link>
          );
        })}
      </div>
      
      <div className="info-box" style={{ marginTop: '40px' }}>
        <h4>📌 Important Notes</h4>
        <ul>
          <li>All calculations are done client-side for your privacy</li>
          <li>Interest rates and tax slabs are updated for FY 2024-25</li>
          <li>You can download PDF reports for all calculations</li>
          <li>This tool is for informational purposes only</li>
        </ul>
      </div>

      <section className="info-box" style={{ marginTop: '28px' }}>
        <h4>Frequently Asked Questions</h4>
        <div style={{ display: 'grid', gap: 12 }}>
          {homeFaqs.map((item) => (
            <details
              key={item.question}
              style={{
                border: '1px solid #dbe3f0',
                borderRadius: 12,
                background: '#fff',
                padding: '14px 16px',
              }}
            >
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#0f172a' }}>
                {item.question}
              </summary>
              <p style={{ margin: '12px 0 0', color: '#475569', lineHeight: 1.65 }}>
                {item.answer}
              </p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;

